import React, { useEffect } from 'react';
import {Box, Divider, Typography} from '@mui/material';
import {Controller, Control, FieldErrors, useWatch, UseFormSetValue} from 'react-hook-form';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import {FormValues} from '../schema';
import {t} from 'i18next';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

type KV = {key: string; displayValue: string};

const SOURCE_FILTERS = ['ice'];

const getFilteredSourceSystem = (sources: KV[]): KV[] => {
  let updatedSources = sources.filter(
    (source) =>
      !SOURCE_FILTERS.includes(source.displayValue?.toLowerCase() ?? '')
  );
  return updatedSources ?? sources;
};

const SourceLocationSection: React.FC<{
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  sourceSystem: KV[];
  serverLocation: KV[];
  options: ISelectItem[];
  ingestionType: ISelectItem[];
  disabled?: boolean;
  clearErrors: (
    name?: keyof FormValues | keyof FormValues[] | undefined
  ) => void;
  setValue: UseFormSetValue<FormValues>;
  isSubmitting: boolean;
  onNext: () => void;
  isEditFlow?: boolean;
  isCopyFlow?: boolean;
}> = ({
  control,
  errors,
  sourceSystem,
  serverLocation,
  options,
  ingestionType,
  disabled = false,
  clearErrors,
  setValue,
  isSubmitting,
  onNext,
  isEditFlow = false,
  isCopyFlow = false,
}) => {
  // Watch the ingestionType field to determine if source system should be disabled
  const selectedIngestionType = useWatch({
    control,
    name: 'ingestionType',
  });

  // Disable source system if ingestionType is "Overlay Only" (key === '2')
  const isIngestionTypeOverlay =
    selectedIngestionType?.key === 'Overlay' ||
    selectedIngestionType?.key === '2';
  const isSourceSystemDisabled = disabled || isIngestionTypeOverlay;

  const focusDescription = () =>
    (
      document.getElementById('ingestion-overlay-description') as HTMLElement
    )?.focus();

  const focusInfoIcon = () =>
    (
      document.querySelector(
        '[data-testid="source-selection-info-icon"]'
      ) as HTMLElement
    )?.focus();

      // Sync pre-selected ingestionType from API into form state on mount
  useEffect(() => {
    if (isEditFlow || isCopyFlow) return;

    const preSelected = ingestionType.find(
      (item: any) => item.isSelected === true
    );
    if (preSelected) {
      setValue(
        'ingestionType',
        {key: String(preSelected.key ?? ''), displayValue: String(preSelected.displayValue ?? '')},
        {shouldValidate: false}
      );
    }
  }, [ingestionType, isEditFlow, isCopyFlow, setValue]);
  return (
    <Box className="section-block" component="section">
      <Controller
        name="ingestionType"
        control={control}
        render={({field}) => (
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'ingestions.Wizard.sourceLocationSection.ingestionType'
            )}
            searchable
            dropdownItems={ingestionType}
            dropdownItemDisplay="displayValue"
            dropdownItemKey="key"
            selectedValues={field.value ?? null}
            onSelectionChange={(val) => {
              field.onChange(val ?? null);
              if (val?.key === '2') clearErrors('sourceSystem');
            }}
            error={!!errors?.ingestionType}
            errorText={(errors?.ingestionType?.message as string) ?? undefined}
            disabled={disabled}
            dataTestId="asd-ingestion-type-dropdown"
            selectRestProps={{
              'aria-label': t(
                'ingestions.Wizard.ariaLabels.sourceLocationSection.ingestionTypeDropdown'
              ),
              'aria-required': true,
              'aria-invalid': !!errors?.ingestionType,
              'aria-describedby': errors?.ingestionType
                ? 'ingestion-type-error'
                : undefined,
            }}
            inputLabelProps={{
              'aria-label': t(
                'ingestions.Wizard.sourceLocationSection.ingestionType'
              ).replace('*', ', required'),
            }}
            tabIndex={-1}
          />
        )}
      />
      <div
        tabIndex={isSubmitting || disabled ? 0 : -1}
        className={
          isSubmitting || disabled
            ? 'ingstion-inactive-next-button'
            : 'ingstion-active-next-button'
        }
        data-testid="inactive-next-button"
        aria-label="inactive-next-button"
        onKeyDown={(e: any) => {
          if (isSubmitting || disabled) return;
          if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            focusInfoIcon();
            return;
          }
          if (e.key === 'Tab') {
            e.preventDefault();
            focusDescription();
          }
        }}
      >
        <ArrButton
          variant="primary"
          endIcon={<ArrowForwardIosIcon />}
          type="button"
          size="small"
          disabled={isSubmitting || disabled}
          onClick={onNext}
          onKeyDown={(e: any) => {
            if (e.key === 'Tab' && e.shiftKey) {
              e.preventDefault();
              focusInfoIcon();
              return;
            }
            if (e.key === 'Tab') {
              e.preventDefault();
              focusDescription();
            }
          }}
          tabIndex={-1}
          aria-label={t(
            'ingestions.Wizard.ariaLabels.selectionHeader.nextButton'
          )}
          dataTestId="next-button"
        >
          {t('ingestions.Wizard.btnNext')}
        </ArrButton>
      </div>
      <Divider
        orientation="horizontal"
        flexItem
        className="arr-divider"
        aria-hidden="true"
      />
      <Typography
        className="typo-text subhead4_size section-title"
        component="h3"
        id="source-location-heading"
        tabIndex={0}
        data-testid={`source-location-section-title`}>
        {t('ingestions.Wizard.sourceLocationSection.title')}
      </Typography>
      <Controller
        name="sourceSystem"
        control={control}
        render={({field}) => (
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'ingestions.Wizard.sourceLocationSection.sourceSystem'
            )}
            searchable
            dropdownItems={getFilteredSourceSystem(sourceSystem)}
            dropdownItemDisplay="displayValue"
            dropdownItemKey="key"
            selectedValues={
              isIngestionTypeOverlay ? null : (field.value ?? null)
            }
            onSelectionChange={(val) => field.onChange(val ?? null)}
            error={!!errors?.sourceSystem}
            errorText={(errors?.sourceSystem?.message as string) ?? undefined}
            disabled={isSourceSystemDisabled}
            dataTestId="asd-source-system-dropdown"
            selectRestProps={{
              'aria-label': t(
                'ingestions.Wizard.ariaLabels.sourceLocationSection.sourceSystemDropdown'
              ),
              'aria-required': !isIngestionTypeOverlay,
              'aria-invalid': !!errors?.sourceSystem,
              'aria-describedby': errors?.sourceSystem
                ? 'source-system-error'
                : undefined,
            }}
            inputLabelProps={{
              'aria-label': t(
                'ingestions.Wizard.sourceLocationSection.sourceSystem'
              ).replace('*', ', required'),
            }}
            tabIndex={-1}
          />
        )}
      />
      <Controller
        name="serverLocation"
        control={control}
        render={({field}) => (
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'ingestions.Wizard.sourceLocationSection.sourceLocation'
            )}
            searchable
            dropdownItems={serverLocation}
            dropdownItemDisplay="displayValue"
            dropdownItemKey="key"
            selectedValues={field.value ?? null}
            onSelectionChange={(val) => field.onChange(val ?? null)}
            error={!!errors?.serverLocation}
            errorText={(errors?.serverLocation?.message as string) ?? undefined}
            disabled={disabled}
            dataTestId="asd-server-location-dropdown"
            selectRestProps={{
              'aria-label': t(
                'ingestions.Wizard.ariaLabels.sourceLocationSection.sourceLocationDropdown'
              ),
              'aria-required': true,
              'aria-invalid': !!errors?.serverLocation,
              'aria-describedby': errors?.serverLocation
                ? 'server-location-error'
                : undefined,
            }}
            tabIndex={-1}
            inputLabelProps={{
              'aria-label': t(
                'ingestions.Wizard.sourceLocationSection.sourceLocation'
              ).replace('*', ', required'),
            }}
          />
        )}
      />
      <Controller
        name="sourceFolder"
        control={control}
        render={({field}) => (
          <ArrSingleSelectDropdown
            dropdownLabel={t(
              'ingestions.Wizard.sourceLocationSection.sourceFolder'
            )}
            dropdownItems={options}
            searchable
            selectedValues={field.value ?? null}
            onSelectionChange={(val) => field.onChange(val ?? null)}
            dropdownItemDisplay="displayValue"
            dropdownItemKey="key"
            error={!!errors?.sourceFolder}
            errorText={(errors?.sourceFolder?.message as string) ?? undefined}
            disabled={disabled}
            dataTestId="asd-source-folder-dropdown"
            selectRestProps={{
              'aria-label': t(
                'ingestions.Wizard.ariaLabels.sourceLocationSection.sourceFolderDropdown'
              ),
              'aria-required': true,
              'aria-invalid': !!errors?.sourceFolder,
              'aria-describedby': errors?.sourceFolder
                ? 'source-folder-error'
                : undefined,
            }}
            inputLabelProps={{
              'aria-label': t(
                'ingestions.Wizard.sourceLocationSection.sourceFolder'
              ).replace('*', ', required'),
            }}
            tabIndex={-1}
          />
        )}
      />
    </Box>
  );
};

export default SourceLocationSection;
